import React, { useState, useEffect } from 'react';
import { 
  getAllRoomTypes,
  getRoomPhotos,
  getAllRoomPhotos,
  createRoomPhoto, 
  deleteRoomPhoto 
} from '../../api';

const RoomPhotoManagement = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [roomPhotos, setRoomPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    room_type_id: '',
    photo: null
  });

  // Helper function to get full image URL
  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return '';
    // If it's already a full URL, return as is
    if (photoUrl.startsWith('http')) return photoUrl;
    // If it's a relative path, prepend the API base URL
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL.replace('/api', '');
    return `${API_BASE_URL}${photoUrl}`;
  };

  useEffect(() => {
    fetchRoomTypes();
    // Load all photos initially when "All Room Types" is selected by default
    fetchRoomPhotos('');
  }, []);

  useEffect(() => {
    fetchRoomPhotos(selectedRoomType);
  }, [selectedRoomType]);

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await getAllRoomTypes();
      setRoomTypes(data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomPhotos = async (roomTypeId) => {
    try {
      let data;
      if (roomTypeId === '' || roomTypeId === null || roomTypeId === undefined) {
        // Fetch all room photos when "All Room Types" is selected
        data = await getAllRoomPhotos();
      } else {
        // Fetch photos for specific room type
        data = await getRoomPhotos(roomTypeId);
      }
      setRoomPhotos(data);
    } catch (error) {
      console.error('Error fetching room photos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create new photo with file upload
      const formDataToSend = new FormData();
      formDataToSend.append('room_type_id', formData.room_type_id);
      formDataToSend.append('photo', formData.photo);
      
      await createRoomPhoto(formDataToSend);
      
      // Always refresh the photos after successful upload
      fetchRoomPhotos(selectedRoomType);
      resetForm();
      showNotification('Room photo uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error saving room photo:', error);
      showNotification('Error uploading room photo. Please try again.', 'error');
    }
  };

  const handleDelete = (photo) => {
    setItemToDelete(photo);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRoomPhoto(itemToDelete.id);
      // Always refresh the photos after successful deletion
      fetchRoomPhotos(selectedRoomType);
      showNotification('Room photo deleted successfully!', 'success');
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting room photo:', error);
      showNotification('Error deleting room photo. Please try again.', 'error');
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      room_type_id: '',
      photo: null
    });
    setEditingPhoto(null);
    setShowForm(false);
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType ? roomType.name : 'Unknown';
  };

  if (loading) {
    return <div className="loading">Loading room types...</div>;
  }

  return (
    <div className="room-photo-management">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="room-photo-management-header">
        <h2>Room Photo Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add New Photo
        </button>
      </div>

      <div className="room-selector">
        <label>Filter by Room Type:</label>
        <select
          value={selectedRoomType}
          onChange={(e) => setSelectedRoomType(e.target.value)}
        >
          <option value="">All Room Types</option>
          {roomTypes.map(roomType => (
            <option key={roomType.id} value={roomType.id}>
              {roomType.name}
            </option>
          ))}
        </select>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add New Photo</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Room Type:</label>
                <select
                  value={formData.room_type_id}
                  onChange={(e) => setFormData({...formData, room_type_id: e.target.value})}
                  required
                >
                  <option value="">Select Room Type</option>
                  {roomTypes.map(roomType => (
                    <option key={roomType.id} value={roomType.id}>
                      {roomType.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Photo File:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({...formData, photo: e.target.files[0]})}
                  required
                />
                <small>Maximum file size: 10MB</small>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Upload
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this room photo?</p>
            <p>This action cannot be undone.</p>
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Delete Photo
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="room-photos-grid">
        {roomPhotos.length === 0 ? (
          <p>No photos found. {selectedRoomType ? 'Select a different room type or add photos.' : 'Add photos to get started.'}</p>
        ) : (
          roomPhotos.map(photo => (
            <div key={photo.id} className="photo-card">
              <div className="photo-image">
                <img src={getImageUrl(photo.url)} alt={`${getRoomTypeName(photo.room_type_id)} Room`} />
              </div>
              <div className="photo-info">
                <p><strong>Room Type:</strong> {getRoomTypeName(photo.room_type_id)}</p>
                <p><strong>ID:</strong> {photo.id}</p>
                <div className="photo-actions">
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(photo)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RoomPhotoManagement;
